<template>
    <Spinner v-if="displaySpinner" />
    <InfoMessage
        title="Welcome to your domain"
        storageKey="welcome-message-domain"
        :forceShow="!hasAnyContent"
        showToggle
    >
        <p>
            A domain helps you organize related products and automation
            processes. Think of a domain as a small business within your
            organization as it provides products and/or services to the outside
            world. This outside world can be other domains within your
            organization, or externally.
        </p>
        <p v-if="products.length === 0">
            Start by creating your first product or service. Products and
            services are what the domain offers to the outside world.
        </p>
    </InfoMessage>

    <Page>
        <template #title>
            <EditableTitle v-model="domain" icon="pi pi-cloud" />
        </template>
        <template #controls>
            <LastChangedInfo
                v-if="domain?.identity"
                :entityId="domain.identity"
                :entityType="EntityType.DOMAIN"
            />
            <Button
                v-if="description && !description.content && isEditing"
                v-tooltip.bottom="{
                    value: 'Will generate a first draft of the description based on the context.',
                    showDelay: 100,
                }"
                severity="secondary"
                icon="pi pi-sparkles"
                label="Generate first draft"
                @click="generateDomainDescription"
            />
        </template>

        <Section class="description" editable>
            <TextAppArtefact
                v-if="description"
                v-model="description"
                :isEditing="isEditing"
            />
        </Section>

        <Section editable>
            <ProductsList :isEditing="isEditing" :products="products" />
        </Section>

        <Section editable>
            <EntitiesList :isEditing="isEditing" :entities="entities" />
        </Section>

        <Section editable>
            <RolesList :isEditing="isEditing" :roles="roles" />
        </Section>

        <Section editable>
            <Documents
                v-if="domain"
                entityType="domain"
                :entity="domain"
                :isEditing="isEditing"
                :artefacts="artefacts"
            />
        </Section>
    </Page>
</template>

<script setup lang="ts">
import { computed } from "vue";

import Documents from "@/components/Documents.vue";
import EditableTitle from "@/components/EditableTitle.vue";
import InfoMessage from "@/components/InfoMessage.vue";
import LastChangedInfo from "@/components/LastChangedInfo.vue";
import Page from "@/components/Page.vue";
import ProductsList from "@/components/Products/ProductsList.vue";
import RolesList from "@/components/Roles/RolesList.vue";
import Section from "@/components/Section.vue";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import EntitiesList from "@/components/entities/EntitiesList.vue";
import { useDomainManager } from "@/composables/domain-manager";
import { useEditManager } from "@/composables/edit-manager";
import { useGenerateDescriptionDialog } from "@/composables/generate-description";
import { useArtefactsManager } from "@/lib/artefacts/user";
import Spinner from "@/shared/Spinner.vue";
import { useBusinessEntitiesManager } from "@/stores/business-entities-store";
import { EntityType } from "@/stores/entity-store";
import { useProductsManager } from "@/stores/products-store";
import { useRolesManager } from "@/stores/roles-store";

const { artefacts } = useArtefactsManager();
const { products } = useProductsManager();
const { entities } = useBusinessEntitiesManager();
const { roles } = useRolesManager();
const { isEditing } = useEditManager();
const { domain, isFetching, description } = useDomainManager();
const { generateContent } = useGenerateDescriptionDialog();

const displaySpinner = computed(() => isFetching.value);

const generateDomainDescription = async () => {
    if (!domain.value) {
        return;
    }
    await generateContent(
        domain.value.identity,
        EntityType.DOMAIN,
        description.value?.updateContent.bind(description.value)
    );
};

const hasAnyContent = computed(() => {
    return Boolean(
        products.value?.length > 0 ||
            description.value?.content ||
            entities.value?.length > 0 ||
            artefacts.value?.length > 0 ||
            (roles.value && roles.value.length > 1)
    );
});
</script>
